import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, doc, updateDoc, getDoc, addDoc, deleteDoc, deleteField } from '@angular/fire/firestore';
import { Observable, from, shareReplay } from 'rxjs';

export interface ProjectImageVariants {
  original?: string;
  webp1400?: string;
  webp600?: string;
  webp200?: string;
}

export interface GalleryImage {
  url: string;
  variants?: ProjectImageVariants;
}

export interface Project {
  id?: string;
  title: string;
  title_nl?: string;
  title_en?: string;
  title_de?: string;
  title_fr?: string;
  title_es?: string;
  short_description?: string;
  short_description_nl?: string;
  short_description_en?: string;
  short_description_de?: string;
  short_description_fr?: string;
  short_description_es?: string;
  description?: string;
  description_nl?: string;
  description_en?: string;
  description_de?: string;
  description_fr?: string;
  description_es?: string;
  image?: string;
  imageVariants?: ProjectImageVariants;
  gallery?: GalleryImage[];
  beforeImages?: GalleryImage[];
  afterImages?: GalleryImage[];
  youtube?: string;
  // Project details
  location?: string;
  location_nl?: string;
  location_en?: string;
  location_de?: string;
  location_fr?: string;
  location_es?: string;
  client?: string;
  client_nl?: string;
  client_en?: string;
  client_de?: string;
  client_fr?: string;
  client_es?: string;
  application?: string;
  application_nl?: string;
  application_en?: string;
  application_de?: string;
  application_fr?: string;
  application_es?: string;
  plantingScheme?: string;
  plantingScheme_nl?: string;
  plantingScheme_en?: string;
  plantingScheme_de?: string;
  plantingScheme_fr?: string;
  plantingScheme_es?: string;
  extra?: string;
  extra_nl?: string;
  extra_en?: string;
  extra_de?: string;
  extra_fr?: string;
  extra_es?: string;
  // SEO
  seo_title?: string;
  seo_title_nl?: string;
  seo_title_en?: string;
  seo_title_de?: string;
  seo_title_fr?: string;
  seo_title_es?: string;
  seo_description?: string;
  seo_description_nl?: string;
  seo_description_en?: string;
  seo_description_de?: string;
  seo_description_fr?: string;
  seo_description_es?: string;
  seo_keywords?: string;
  seo_keywords_nl?: string;
  seo_keywords_en?: string;
  seo_keywords_de?: string;
  seo_keywords_fr?: string;
  seo_keywords_es?: string;
  // Related projects
  relatedProjectIds?: string[];
  slug?: string;
  mainCategory?: string;
  categories?: string[];
  tags?: string[];
  url?: string;
  featured?: boolean;
  domains?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private projectsCache$: Observable<Project[]> | null = null;

  constructor(private firestore: Firestore) {}

  getProjects(): Observable<Project[]> {
    if (!this.projectsCache$) {
      const projectsRef = collection(this.firestore, 'dwt_projects');
      const projectsData$ = collectionData(projectsRef, { idField: 'id' }) as Observable<Project[]>;
      this.projectsCache$ = projectsData$.pipe(
        shareReplay(1)
      );
    }
    return this.projectsCache$;
  }

  invalidateCache(): void {
    this.projectsCache$ = null;
  }

  async getProjectById(projectId: string): Promise<Project | null> {
    const projectRef = doc(this.firestore, 'dwt_projects', projectId);
    const projectSnap = await getDoc(projectRef);
    
    if (projectSnap.exists()) {
      return { id: projectSnap.id, ...projectSnap.data() } as Project;
    }
    return null;
  }

  async createProject(projectData: Omit<Project, 'id'>): Promise<string> {
    const projectsRef = collection(this.firestore, 'dwt_projects');
    const docRef = await addDoc(projectsRef, {
      ...projectData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    this.invalidateCache();
    return docRef.id;
  }

  async updateProject(projectId: string, projectData: Partial<Project>): Promise<void> {
    const projectRef = doc(this.firestore, 'dwt_projects', projectId);
    const updateData: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(projectData)) {
      if (value === undefined) {
        updateData[key] = deleteField();
      } else {
        updateData[key] = value;
      }
    }
    updateData['updatedAt'] = new Date();
    await updateDoc(projectRef, updateData as any);
    this.invalidateCache();
  }

  async deleteProject(projectId: string): Promise<void> {
    const projectRef = doc(this.firestore, 'dwt_projects', projectId);
    await deleteDoc(projectRef);
    this.invalidateCache();
  }

  async updateProjectDomains(projectId: string, domains: string[]): Promise<void> {
    const projectRef = doc(this.firestore, 'dwt_projects', projectId);
    await updateDoc(projectRef, { domains: domains });
    this.invalidateCache();
  }
}
